import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiCheckCircle, 
  FiShield, 
  FiRefreshCw, 
  FiHelpCircle,
  FiAlertCircle,
  FiStar,
  FiInfo,
  FiEdit,
  FiXCircle,
  FiClock,
  FiLock,
  FiArrowRight,
  FiTrendingUp,
  FiEye,
  FiTarget,
  FiArrowLeft,
  FiPercent,
  FiZap,
  FiCalendar
} from 'react-icons/fi';
import { MdVerified, MdLocalOffer } from 'react-icons/md';
import Button from '../../../components/common/Button';
import Loading from '../../../components/common/Loading';
import { useAuth } from '../../../context/AuthContext';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import { 
  getBoostPackages, 
  getListingBoosts,
  getUserBoostHistory,
  cancelBoostOrder,
  checkCanChangeBoost,
  verifyBoostPayment
} from '../../../services/api';


const BoostPackage = ({ 
  listingId: propListingId,
  onBoostSuccess 
}) => {
  const navigate = useNavigate();
  const { id: urlListingId } = useParams();
  const { user } = useAuth();
  
  const listingId = propListingId || parseInt(urlListingId);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [error, setError] = useState(null);
  
  const [activeBoost, setActiveBoost] = useState(null);
  const [pendingBoostOrder, setPendingBoostOrder] = useState(null);
  const [showPendingNotice, setShowPendingNotice] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    fetchPackages();
    checkExistingBoosts();
  }, [listingId, user?.user_id]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBoostPackages();
      setPackages(response.data || []);
    } catch (error) {
      // console.error('Error fetching boost packages:', error);
      setError('Failed to load boost packages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingBoosts = async () => {
    try {
      const boostsResponse = await getListingBoosts(listingId);
      if (boostsResponse.success && boostsResponse.data) {
        const active = boostsResponse.data.find(
          boost => boost.status === 'active' && new Date(boost.expires_at) > new Date()
        );
        if (active) {
          setActiveBoost(active);
        }
      }

      if (user?.user_id) {
        const historyResponse = await getUserBoostHistory(user.user_id);
        if (historyResponse.success && historyResponse.data) {
          const pendingOrders = historyResponse.data.filter(
            order => order.payment_status === 'pending' && 
                     order.listing_id === listingId
          );
          
          if (pendingOrders.length > 0) {
            const latestPending = pendingOrders[0];
            setPendingBoostOrder(latestPending);
            setShowPendingNotice(true);
            await verifyPendingPaymentStatus(latestPending.order_id);
          }
        }
      }
    } catch (error) {
      // console.error('Error checking existing boosts:', error);
    }
  };

  const verifyPendingPaymentStatus = async (orderId) => {
    try {
      const statusResponse = await verifyBoostPayment(orderId);
      if (statusResponse.success && statusResponse.data_mismatch) {
        setError(`⚠️ Payment was successful but boost wasn't activated. Please contact support.`);
      }
    } catch (error) {
      // console.warn('Could not verify payment status:', error);
    }
  };

  const handleSelectPackage = (pkg) => {
    if (pendingBoostOrder && !showPendingNotice) {
      setShowPendingNotice(true);
      return;
    }
    setSelectedPackage(pkg);
    setError(null);
  };

  const handleCancelPendingOrder = async () => {
    if (!pendingBoostOrder || cancellingOrder) return;
    
    setCancellingOrder(true);
    try {
      const response = await cancelBoostOrder(pendingBoostOrder.order_id, user.user_id);
      if (response.success) {
        setPendingBoostOrder(null);
        setShowPendingNotice(false);
        alert('✅ Pending boost order cancelled successfully.');
        fetchPackages();
      } else {
        throw new Error(response.error || 'Failed to cancel order');
      }
    } catch (error) {
      // console.error('Error cancelling order:', error);
      setError(`Failed to cancel pending order: ${error.message}`);
    } finally {
      setCancellingOrder(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!selectedPackage || !user) {
      setError('Please select a package and ensure you are logged in.');
      return;
    }

    navigate(`/store/boost/payment/${listingId}`, {
      state: {
        package: selectedPackage,
        userId: user.user_id,
        userEmail: user.email,
        pendingOrderId: pendingBoostOrder?.order_id
      }
    });
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loading size="large" />
          <p className="mt-4 text-gray-600">Loading boost packages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <FiArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Boost Your Listing</h1>
                <p className="text-gray-600 mt-1">Increase visibility and get more sales</p>
              </div>
            </div>
            <button
              onClick={() => setShowInfoModal(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <FiInfo className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Active Boost Banner */}
        {activeBoost && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FiTrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-green-900 text-lg">Boost Active! 🚀</h3>
                <p className="text-green-800 mt-1">
                  Your listing is already boosted with <span className="font-semibold">{activeBoost.package_name}</span> 
                  until {formatDate(activeBoost.expires_at)}.
                </p>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-green-700">Boost Progress</span>
                    <span className="font-semibold text-green-900">
                      {Math.ceil((new Date(activeBoost.expires_at) - new Date()) / (1000 * 60 * 60 * 24))} days left
                    </span>
                  </div>
                  <div className="h-2 bg-green-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.max(10, Math.min(100, 
                          ((new Date(activeBoost.expires_at) - new Date()) / 
                          (activeBoost.duration_days * 24 * 60 * 60 * 1000)) * 100
                        ))}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending Order Notice */}
        {showPendingNotice && pendingBoostOrder && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FiClock className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-amber-900 text-lg">Pending Boost Order</h3>
                <div className="mt-4 bg-white/80 border border-amber-200 rounded-xl p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-gray-600">Order ID:</div>
                    <div className="font-semibold text-right">#{pendingBoostOrder.order_id}</div>
                    
                    <div className="text-gray-600">Amount:</div>
                    <div className="font-semibold text-right text-orange-600 text-lg">
                      {formatPrice(pendingBoostOrder.amount)}
                    </div>
                    
                    <div className="text-gray-600">Created:</div>
                    <div className="text-right text-sm">{formatDate(pendingBoostOrder.created_at)}</div>
                  </div>
                </div>
                
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowPendingNotice(false)}
                    className="p-4 bg-white border-2 border-blue-300 rounded-xl text-left hover:bg-blue-50 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <FiEdit className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-blue-900">Select new package</div>
                        <div className="text-sm text-blue-700">
                          Cancel existing and choose different package
                        </div>
                      </div>
                    </div>
                    <FiArrowRight className="w-5 h-5 text-blue-500 group-hover:translate-x-1 transition-transform" />
                  </button>
                  
                  <button
                    onClick={handleCancelPendingOrder}
                    disabled={cancellingOrder}
                    className="p-4 bg-white border-2 border-gray-300 rounded-xl text-left hover:bg-gray-50 transition-all disabled:opacity-50 flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        {cancellingOrder ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
                        ) : (
                          <FiXCircle className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {cancellingOrder ? 'Cancelling...' : 'Cancel order'}
                        </div>
                        <div className="text-sm text-gray-700">
                          Cancel this boost request
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Packages Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Select a Boost Package</h2>
            <span className="text-sm text-gray-600">{packages.length} packages available</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map((pkg) => {
              const features = pkg.features ? JSON.parse(pkg.features) : [];
              const isSelected = selectedPackage?.package_id === pkg.package_id;
              const isPopular = pkg.is_popular;
              
              return (
                <div
                  key={pkg.package_id}
                  className={`relative border-2 rounded-2xl p-6 transition-all cursor-pointer hover:shadow-lg ${
                    isSelected
                      ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${isPopular ? 'border-orange-300' : ''}`}
                  onClick={() => handleSelectPackage(pkg)}
                >
                  {/* Popular Badge */}
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg whitespace-nowrap flex items-center gap-1">
                        <FiStar className="w-3 h-3" />
                        MOST POPULAR
                      </span>
                    </div>
                  )}

                  {/* Package Header */}
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full mb-3">
                      <FiZap className="w-4 h-4 text-orange-500" />
                      <span className="text-sm font-semibold text-gray-700">{pkg.duration_days} Days</span>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{pkg.name}</h3>
                    <p className="text-gray-500 text-sm mb-4">{pkg.description}</p>
                    
                    <div className="mb-2">
                      <span className="text-3xl font-bold text-orange-600">
                        {formatPrice(pkg.price)}
                      </span>
                      {pkg.original_price && pkg.original_price > pkg.price && (
                        <div className="mt-2">
                          <span className="text-sm text-gray-400 line-through mr-2">
                            {formatPrice(pkg.original_price)}
                          </span>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">
                            Save {formatPrice(pkg.original_price - pkg.price)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">What's Included:</h4>
                    <ul className="space-y-2">
                      {features.slice(0, 4).map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Selection Indicator */}
                  <div className="text-center">
                    {isSelected ? (
                      <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-lg">
                        <FiCheckCircle className="w-4 h-4" />
                        <span className="font-semibold">Selected</span>
                      </div>
                    ) : (
                      <button className="w-full py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors">
                        Select Package
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Package Summary */}
        {selectedPackage && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <FiZap className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Selected Package</h3>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-2xl font-bold text-orange-600">
                        {formatPrice(selectedPackage.price)}
                      </span>
                      <span className="text-gray-500">
                        • {selectedPackage.duration_days} days • {selectedPackage.name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedPackage(null)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Change
                </button>
                <button
                  onClick={handleProceedToPayment}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 font-semibold transition-all flex items-center justify-center gap-2 shadow-md"
                >
                  <span>Proceed to Payment</span>
                  <FiArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-8 mb-8">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-blue-900 mb-2">Why Boost Your Listing?</h2>
            <p className="text-blue-800">Get up to 10x more views and faster sales</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <FiEye className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-blue-900 text-lg mb-2">Increased Visibility</h3>
              <p className="text-blue-800 text-sm">
                Your listing appears at the top of search results and category pages
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <FiTarget className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-green-900 text-lg mb-2">Priority Placement</h3>
              <p className="text-green-800 text-sm">
                Get featured placement for maximum exposure to potential buyers
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                <FiShield className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-purple-900 text-lg mb-2">Trust & Credibility</h3>
              <p className="text-purple-800 text-sm">
                Boosted listings receive 3x more trust from buyers
              </p>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Boost Performance Stats</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-orange-600 mb-1">5-10x</div>
              <div className="text-sm text-gray-600">More Views</div>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-green-600 mb-1">3x</div>
              <div className="text-sm text-gray-600">Faster Sales</div>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-blue-600 mb-1">87%</div>
              <div className="text-sm text-gray-600">Satisfaction Rate</div>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-xl">
              <div className="text-2xl font-bold text-purple-600 mb-1">24/7</div>
              <div className="text-sm text-gray-600">Active Boost</div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">How long does boosting last?</h3>
              <p className="text-gray-600">
                Boost duration is based on your selected package (7, 14, or 30 days). It starts immediately after payment confirmation.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Can I cancel my boost?</h3>
              <p className="text-gray-600">
                Boosts are non-refundable and cannot be cancelled once activated. Pending orders can be cancelled before payment.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">When will I see results?</h3>
              <p className="text-gray-600">
                Your listing gets boosted immediately. Most sellers see increased views within the first hour.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">About Boost Packages</h3>
              <button
                onClick={() => setShowInfoModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <FiXCircle className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-gray-600">
                Boost packages increase your listing's visibility by featuring it at the top of search results and category pages.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">💡 Tips for Best Results:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Ensure your listing has clear, high-quality photos</li>
                  <li>• Set a competitive price</li>
                  <li>• Write a detailed description</li>
                  <li>• Respond quickly to buyer inquiries</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default BoostPackage;